import {union, without} from 'lodash';

import {TablePaginationTypesEnum} from '../../../../../models/table/tablePaginationDataModel';
import {AppAction} from '../../../../appActions';
import {AccountsActionsEnum} from '../../../../data/accounts/accountsActions';
import {CreateAccountFormActionsEnum} from '../../../accounts/createAccountForm/createAccountFormActions';
import {ContactsManagerFileActionsEnum} from '../file/contactsManagerFileStateActions';
import {ContactsManagerTableActionsEnum} from './contactsManagerTableActions';
import {
  ContactsManagerAccountsTableState,
  ContactsManagerTableState,
  ContactsManagerTableStatusesEnum,
  ContactsManagerTableTypesEnum,
  tableStateIsAccountContactsTableState,
  tableStateIsAccountsTableState,
  tableStateIsContactsTableState,
  tableStateIsListsTableState,
  tableStateIsSearchCardGroupsTableState,
} from './contactsManagerTableState';

export function contactsManagerTableReducer(
  state: ContactsManagerTableState | undefined,
  action: AppAction,
): ContactsManagerTableState | undefined {
  switch (action.type) {
    // If an account is deleted, we will remove it from the state
    case AccountsActionsEnum.ACCOUNT_DELETED: {
      if (!state || !tableStateIsAccountsTableState(state)) {
        return state;
      }
      return {
        ...state,
        accountIds: state.accountIds.filter((accountId) => accountId !== action.payload.accountId),
        totalResults: (state.totalResults ?? 1) - 1,
      };
    }

    // If we successfully uploaded a file, we can assume we need to reload the table.
    case ContactsManagerFileActionsEnum.UPLOAD_COMPLETE: {
      if (
        !state ||
        !tableStateIsAccountsTableState(state) ||
        action.payload.dryRun ||
        action.payload.isV2Upload
      ) {
        return state;
      }
      return {
        ...state,
        status: ContactsManagerTableStatusesEnum.FETCH_REQUIRED,
      };
    }
    // Reset the account table state and trigger a table refresh
    case CreateAccountFormActionsEnum.CREATE_ACCOUNT_COMPLETED: {
      if (!state || !tableStateIsAccountsTableState(state)) {
        return state;
      }
      return buildInitialAccountsTableState();
    }
    case ContactsManagerTableActionsEnum.SET_UP_ACCOUNTS_TABLE:
      return buildInitialAccountsTableState();
    case ContactsManagerTableActionsEnum.SET_UP_CONTACTS_TABLE:
      return {
        type: ContactsManagerTableTypesEnum.CONTACTS,
        status: ContactsManagerTableStatusesEnum.LOADING,
        cardIds: [],
        totalResults: undefined,
        selectedCardIds: action.payload.selectedCardIds ?? [],
        isInMultiSelectMode: (action.payload.selectedCardIds ?? []).length > 1,
      };
    case ContactsManagerTableActionsEnum.SET_UP_LISTS_TABLE:
      return {
        type: ContactsManagerTableTypesEnum.LISTS,
        status: ContactsManagerTableStatusesEnum.LOADING,
        cardGroupIds: [],
        totalResults: undefined,
        selectedCardGroupIds: action.payload.selectedCardGroupIds ?? [],
        isInMultiSelectMode: (action.payload.selectedCardGroupIds ?? []).length > 1,
      };
    case ContactsManagerTableActionsEnum.SET_UP_SEARCH_CARD_GROUPS_TABLE:
      return {
        type: ContactsManagerTableTypesEnum.SEARCH_CARD_GROUPS,
        status: ContactsManagerTableStatusesEnum.LOADING,
        cardGroupIds: [],
        totalResults: undefined,
        selectedCardGroupIds: action.payload.selectedCardGroupIds ?? [],
        isInMultiSelectMode: (action.payload.selectedCardGroupIds ?? []).length > 1,
      };

    case ContactsManagerTableActionsEnum.ADD_ACCOUNT_DETAILS: {
      if (!state || !tableStateIsAccountsTableState(state)) {
        return state;
      }
      return {
        ...state,
        status: ContactsManagerTableStatusesEnum.LOADED,
        accountIds: union(state.accountIds, action.payload.accountIds),
        totalResults: action.payload.totalResults,
      };
    }
    case ContactsManagerTableActionsEnum.ADD_SELECTED_ACCOUNT_IDS: {
      if (!state || !tableStateIsAccountsTableState(state)) {
        return state;
      }
      return {
        ...state,
        selectedAccountIds: union(state.selectedAccountIds, action.payload.accountIds),
      };
    }
    case ContactsManagerTableActionsEnum.REMOVE_SELECTED_ACCOUNT_IDS: {
      if (!state || !tableStateIsAccountsTableState(state)) {
        return state;
      }
      return {
        ...state,
        selectedAccountIds: without(state.selectedAccountIds, ...action.payload.accountIds),
      };
    }

    case ContactsManagerTableActionsEnum.SET_UP_ACCOUNT_CONTACTS_TABLE:
      return {
        type: ContactsManagerTableTypesEnum.ACCOUNT_CONTACTS,
        accountId: action.payload.accountId,
        status: ContactsManagerTableStatusesEnum.LOADING,
        cardIds: [],
        totalResults: undefined,
        selectedCardIds: [],
        isInMultiSelectMode: false,
      };

    case ContactsManagerTableActionsEnum.OFFSET_PAGINATED_TABLE_FETCHED: {
      if (tableStateIsContactsTableState(state) || tableStateIsAccountContactsTableState(state)) {
        return {
          ...state,
          paginationData: {
            type: TablePaginationTypesEnum.OFFSET,
            canLoadMore: action.payload.canLoadMore,
            offset: action.payload.offset,
          },
        };
      }
      return state;
    }
    case ContactsManagerTableActionsEnum.TOKEN_PAGINATED_TABLE_FETCHED: {
      // If a type is supplied, we only want to update the pagination data if the types match.
      if (action.payload.type && action.payload.type !== state?.type) {
        return state;
      }
      if (
        tableStateIsAccountsTableState(state) ||
        tableStateIsListsTableState(state) ||
        tableStateIsSearchCardGroupsTableState(state)
      ) {
        return {
          ...state,
          paginationData: {
            type: TablePaginationTypesEnum.TOKEN,
            canLoadMore: action.payload.canLoadMore,
            token: action.payload.token,
          },
        };
      }
      return state;
    }

    case ContactsManagerTableActionsEnum.ADD_CARD_ID_REFS: {
      if (tableStateIsAccountContactsTableState(state) || tableStateIsContactsTableState(state)) {
        return {
          ...state,
          cardIds: union(state.cardIds, action.payload.cardIds),
          status: ContactsManagerTableStatusesEnum.LOADED,
        };
      }
      return state;
    }
    case ContactsManagerTableActionsEnum.ADD_CARD_GROUP_ID_REFS: {
      if (!tableStateIsListsTableState(state) && !tableStateIsSearchCardGroupsTableState(state)) {
        return state;
      }
      return {
        ...state,
        cardGroupIds: union(state.cardGroupIds, action.payload.cardGroupIds),
        status: ContactsManagerTableStatusesEnum.LOADED,
      };
    }

    case ContactsManagerTableActionsEnum.ADD_SELECTED_CARD_IDS: {
      if (
        !state ||
        (!tableStateIsContactsTableState(state) && !tableStateIsAccountContactsTableState(state))
      ) {
        return state;
      }
      const selectedCardIds = computeSelectedIds(
        state.selectedCardIds,
        action.payload.cardIds,
        state.isInMultiSelectMode || action.payload.isInMultiSelectMode,
      );
      return {
        ...state,
        selectedCardIds,
        // If we are in multi select mode, the only way to no longer be in multi select mode is to deselect all cards.
        isInMultiSelectMode: computeIsInMultiSelectMode(
          selectedCardIds,
          state.isInMultiSelectMode || action.payload.isInMultiSelectMode,
        ),
      };
    }
    case ContactsManagerTableActionsEnum.REMOVE_SELECTED_CARD_IDS: {
      if (
        !state ||
        (!tableStateIsContactsTableState(state) && !tableStateIsAccountContactsTableState(state))
      ) {
        return state;
      }
      const selectedCardIds = state.selectedCardIds.filter(
        (cardId) => !action.payload.cardIds.includes(cardId),
      );
      return {
        ...state,
        selectedCardIds,
        // If we have no more cards selected, remove the multi select mode, otherwise keep the current state.
        isInMultiSelectMode: selectedCardIds.length === 0 ? false : state.isInMultiSelectMode,
      };
    }

    case ContactsManagerTableActionsEnum.ADD_SELECTED_CARD_GROUP_IDS: {
      if (!state || (!tableStateIsListsTableState(state) && !tableStateIsSearchCardGroupsTableState(state))) {
        return state;
      }
      const selectedCardGroupIds = computeSelectedIds(
        state.selectedCardGroupIds,
        action.payload.cardGroupIds,
        state.isInMultiSelectMode || action.payload.isInMultiSelectMode,
      );
      return {
        ...state,
        selectedCardGroupIds,
        // If we are in multi select mode, the only way to no longer be in multi select mode is to deselect all cardGroups.
        isInMultiSelectMode: computeIsInMultiSelectMode(
          selectedCardGroupIds,
          state.isInMultiSelectMode || action.payload.isInMultiSelectMode,
        ),
      };
    }
    case ContactsManagerTableActionsEnum.REMOVE_SELECTED_CARD_GROUP_IDS: {
      if (!state || (!tableStateIsListsTableState(state) && !tableStateIsSearchCardGroupsTableState(state))) {
        return state;
      }
      const selectedCardGroupIds = state.selectedCardGroupIds.filter(
        (cardGroupId) => !action.payload.cardGroupIds.includes(cardGroupId),
      );
      return {
        ...state,
        selectedCardGroupIds,
        // If we have no more cardGroups selected, remove the multi select mode, otherwise keep the current state.
        isInMultiSelectMode: selectedCardGroupIds.length === 0 ? false : state.isInMultiSelectMode,
      };
    }

    case ContactsManagerTableActionsEnum.CLEANUP:
      return undefined;
    case ContactsManagerTableActionsEnum.FORCE_REFRESH: {
      if (tableStateIsAccountsTableState(state)) {
        return {
          ...state,
          status: ContactsManagerTableStatusesEnum.FETCH_REQUIRED,
          paginationData: undefined,
        };
      }
      if (tableStateIsContactsTableState(state)) {
        return {
          ...state,
          status: ContactsManagerTableStatusesEnum.FETCH_REQUIRED,
          selectedCardIds: [],
          isInMultiSelectMode: false,
          paginationData: undefined,
        };
      }
      if (tableStateIsListsTableState(state) || tableStateIsSearchCardGroupsTableState(state)) {
        return {
          ...state,
          status: ContactsManagerTableStatusesEnum.FETCH_REQUIRED,
          cardGroupIds: [],
          paginationData: undefined,
        };
      }
      return state;
    }

    default:
      return state;
  }
}

/*
 * Helpers.
 */

function computeSelectedIds(
  selectedIds: ReadonlyArray<number>,
  newSelectedIds: ReadonlyArray<number>,
  isInMultiSelectMode: boolean,
) {
  // If we have a single id selected and we are not in multi select mode, return the single value.
  if (selectedIds.length <= 1 && newSelectedIds.length === 1 && !isInMultiSelectMode) {
    return newSelectedIds;
  }
  return union(selectedIds, newSelectedIds);
}

function computeIsInMultiSelectMode(selectedIds: ReadonlyArray<number>, isInMultiSelectMode: boolean) {
  if (isInMultiSelectMode) {
    return true;
  }
  return selectedIds.length > 1;
}

function buildInitialAccountsTableState(): ContactsManagerAccountsTableState {
  return {
    type: ContactsManagerTableTypesEnum.ACCOUNTS,
    status: ContactsManagerTableStatusesEnum.LOADING,
    accountIds: [],
    selectedAccountIds: [],
    totalResults: undefined,
  };
}
